import { useEffect, useState } from 'react';

import { AuthError, errorKey, type AuthApi } from '../auth/api';
import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { NICK_MAX, nickLength } from './nickname';

export interface RenameResult {
  nickname: string;
  renamesLeft: number;
}

/**
 * 改昵称。
 *
 * **不在前端做字符校验。** 后端那条正则挡的是控制字符和各种不可见空白 ——
 * 零宽字符能拼出一个看起来和别人完全一样的名字，用来冒充。前端再写一份的话，
 * 两份实现迟早不一致，而"前端说行、后端说不行"是最难解释给用户听的一种状态。
 * 这里只做两件后端也会做、但提前做体验更好的事：去首尾空白、限长度。
 *
 * **改名有次数上限**（后端 `NICKNAME_RENAMES`）。剩几次写在输入框上面 ——
 * 改之前就该知道这是有限的，而不是用完了才被告知。
 */
export function NicknameSheet(
  { open, api, token, current, renamesLeft, onClose, onDone }:
  {
    open: boolean;
    api: AuthApi;
    token: string;
    current: string | null;
    /** 还能改几次。null = 还没拉到，不拦 */
    renamesLeft: number | null;
    onClose(): void;
    onDone(r: RenameResult): void;
  },
) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // 每次打开都从当前值起步 —— 改名多半是**微调**，不是重起一个。
  // 上次输了一半没提交的内容也不该留到下次。
  useEffect(() => {
    if (open) { setName(current ?? ''); setErr(''); }
  }, [open, current]);

  const trimmed = name.trim();
  const len = nickLength(name);
  const unchanged = trimmed === (current ?? '');
  const exhausted = renamesLeft === 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const r = await api.setNickname(token, trimmed);
      onDone({ nickname: r.nickname, renamesLeft: r.renamesLeft });
      onClose();
    } catch (e2) {
      // BAD_NICKNAME 带一个 reason。**"有看不见的字符"必须单说** ——
      // 一句笼统的"不合法"对着一个看不见的字符等于没说，
      // 用户会把同一串字反复粘进来。
      const reason = e2 instanceof AuthError && e2.code === 'BAD_NICKNAME'
        ? e2.detail?.reason : null;
      setErr(reason === 'chars' ? t('err.NICKNAME_CHARS')
        : reason === 'length' ? t('profile.nicknameHint')
        : t(errorKey(e2)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} title={t('profile.editNickname')} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        {renamesLeft !== null && (
          <p className={exhausted ? 'field__err' : 'small dim'}>
            {exhausted ? t('profile.renamesNone') : t('profile.renamesLeft', { n: renamesLeft })}
          </p>
        )}

        <label className="field">
          <span className="field__label">{t('profile.nickname')}</span>
          <input
            className="field__input"
            /* autoComplete=off + 不用 name="nickname"：这不是登录表单，
               别让密码管理器往里填东西 */
            autoComplete="off"
            value={name}
            maxLength={NICK_MAX * 2}
            disabled={exhausted}
            required
            onChange={(e) => setName(e.target.value)}
          />
          <div className="spread">
            <span className="field__hint">{t('profile.nicknameHint')}</span>
            {/* 计数器按码点数（和后端一致）。超了就变红 —— 不用 maxLength 硬截：
                它按 UTF-16 数，会在 emoji 上提前截断，而且截得悄无声息 */}
            <span className={len > NICK_MAX ? 'field__err num' : 'field__hint num'}>
              {len}/{NICK_MAX}
            </span>
          </div>
        </label>

        {err && <p className="field__err" role="alert">{err}</p>}

        <Button variant="primary" type="submit" className="btn--wide"
                disabled={busy || unchanged || exhausted || len === 0 || len > NICK_MAX}>
          {busy ? t('action.working') : t('action.save')}
        </Button>

        {/* 昵称不唯一，先说清楚 —— 不然改完发现重名会来问 */}
        <p className="small dim">{t('profile.nicknameNotUnique')}</p>
      </form>
    </Sheet>
  );
}
